import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

/**
 * MobilePageDots — Instagram-style dot navigator for mobile only.
 * Fixed at the bottom of the viewport. Each dot represents a main page.
 * Tap → navigate. Optional swipe gesture on any element wrapped with
 * useSwipeToNavigate(pageOrder).
 */
export const PAGE_ORDER = [
  { to: "/", label: "Home", tid: "dot-home" },
  { to: "/location", label: "Location", tid: "dot-location" },
  { to: "/reservation", label: "Reservation", tid: "dot-reservation" },
  { to: "/contact", label: "Contact", tid: "dot-contact" },
];

export default function MobilePageDots() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentIdx = PAGE_ORDER.findIndex((p) => p.to === pathname);
  const [labelIdx, setLabelIdx] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  function handleTap(i) {
    setLabelIdx(i);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setLabelIdx(null), 900);
    if (i !== currentIdx) navigate(PAGE_ORDER[i].to);
  }

  if (currentIdx < 0) return null;

  return (
    <div
      data-testid="mobile-page-dots"
      className="md:hidden fixed bottom-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
    >
      <div className="relative flex flex-col items-center gap-2">
        {/* Floating label preview when tapped */}
        <div
          data-testid="mobile-page-dots-label"
          className={`text-[0.62rem] uppercase tracking-[0.24em] font-medium text-foreground/90 bg-background/85 backdrop-blur-md border border-border px-3 py-1.5 transition-all duration-300 ${
            labelIdx !== null
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-1 pointer-events-none"
          }`}
        >
          {labelIdx !== null ? PAGE_ORDER[labelIdx].label : ""}
        </div>

        <div className="pointer-events-auto flex items-center gap-2.5 px-4 py-2.5 bg-background/80 backdrop-blur-xl border border-border/70 rounded-full shadow-[0_4px_24px_rgba(0,0,0,0.5)]">
          {PAGE_ORDER.map((p, i) => {
            const active = i === currentIdx;
            return (
              <button
                key={p.to}
                data-testid={p.tid}
                aria-label={`Go to ${p.label}`}
                onClick={() => handleTap(i)}
                className={`transition-all duration-300 rounded-full ${
                  active
                    ? "w-5 h-1.5 bg-primary"
                    : "w-1.5 h-1.5 bg-foreground/35 hover:bg-foreground/70"
                }`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * useSwipeToNavigate — attach touch handlers to a page element to swipe
 * between the PAGE_ORDER routes. Returns onTouchStart/onTouchEnd handlers.
 *
 * @param {Object} opts
 * @param {boolean} opts.onlyAtTop — if true, swipe only fires when the page
 *   is scrolled near the top (≤ 30px).
 */
export function useSwipeToNavigate(opts = {}) {
  const { onlyAtTop = false } = opts;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const startRef = useRef(null);

  const currentIdx = PAGE_ORDER.findIndex((p) => p.to === pathname);

  function isAtTop() {
    return typeof window === "undefined" || window.scrollY <= 30;
  }

  function onTouchStart(e) {
    if (onlyAtTop && !isAtTop()) {
      startRef.current = null;
      return;
    }
    const t = e.changedTouches?.[0];
    if (t) startRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  }

  function onTouchEnd(e) {
    const start = startRef.current;
    if (!start || currentIdx < 0) return;
    if (onlyAtTop && !isAtTop()) {
      startRef.current = null;
      return;
    }
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.time;
    startRef.current = null;
    // horizontal, fast, not diagonal
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 700) {
      const dir = dx < 0 ? 1 : -1;
      const nextIdx = currentIdx + dir;
      if (nextIdx >= 0 && nextIdx < PAGE_ORDER.length) {
        navigate(PAGE_ORDER[nextIdx].to);
      }
    }
  }

  return { onTouchStart, onTouchEnd };
}
